/**
 * Viewer version management — TanStack Query hooks.
 *
 * Diffing between versions happens in version-history-dialog.tsx, from the
 * snapshots this list already returns — there is deliberately no diff hook.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from './api-client'
import { useApiBase } from './api-base-provider'
import { invalidateLegacyViewerConfig } from './legacy-viewer-cache'

// Re-use the same prefix pattern from the existing file
function useQueryKeyPrefix() {
  const { baseUrl } = useApiBase()
  return ['v2', baseUrl] as const
}


// ─── Types ───────────────────────────────────────────

export type ViewerVersionEntry = {
  id: string
  viewer: string
  version: number
  snapshot: Record<string, any>
  delta: Record<string, any> | null
  savedBy: { id: string; name: string; email: string } | null
  savedAt: string
  source: 'auto' | 'revert'
  label: string | null
  changeCount: number
}


// ─── Queries ─────────────────────────────────────────

/**
 * Fetch all versions for a viewer (newest first).
 * Returns up to 10 entries with change counts.
 *
 * `refetchOnMount: 'always'` overrides the app-wide `refetchOnMount: false` in
 * queryClient.ts: versions are also created by the in-app editor, outside this
 * cache, so the list has to be re-read every time the dialog opens.
 */
export function useViewerVersions(viewerId: string | null) {
  const api = useApiClient()
  const prefix = useQueryKeyPrefix()
  return useQuery({
    queryKey: [...prefix, 'viewer-versions', viewerId],
    queryFn: async () => {
      const { data } = await api.get(`/api/viewers/${viewerId}/versions`)
      return data as ViewerVersionEntry[]
    },
    enabled: Boolean(viewerId),
    refetchOnMount: 'always',
  })
}


// ─── Mutations ───────────────────────────────────────

/**
 * Revert a viewer to a specific version.
 * This creates a new version entry (source: 'revert') capturing the
 * state before the revert, then applies the target version's snapshot.
 */
export function useRevertVersion() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({ viewerId, version }: { viewerId: string; version: number }) => {
      const { data } = await api.post(`/api/viewers/${viewerId}/versions/${version}/revert`)
      return data
    },
    onSuccess: (_data, { viewerId }) => {
      invalidateLegacyViewerConfig(viewerId)
      // Invalidate version list (new revert entry added)
      qc.invalidateQueries({ queryKey: [...prefix, 'viewer-versions', viewerId] })
      // Invalidate the project view (viewer config changed)
      qc.invalidateQueries({ queryKey: [...prefix, 'project'] })
      // Invalidate any open config panel
      qc.invalidateQueries({ queryKey: [...prefix, 'app-config'] })
    },
  })
}

/**
 * Update the label of a version entry.
 */
export function useLabelVersion() {
  const api = useApiClient()
  const qc = useQueryClient()
  const prefix = useQueryKeyPrefix()
  return useMutation({
    mutationFn: async ({
                         viewerId,
                         version,
                         label,
                       }: {
      viewerId: string
      version: number
      label: string | null
    }) => {
      const { data } = await api.patch(`/api/viewers/${viewerId}/versions/${version}`, { label })
      return data
    },
    onSuccess: (_data, { viewerId }) => {
      qc.invalidateQueries({ queryKey: [...prefix, 'viewer-versions', viewerId] })
    },
  })
}
